import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { useIntl } from 'ha/i18n';
import { LongParagraph } from 'ha/ui.legacy/LongParagraph';
import {
  showRegistration,
  getLocalRegistrationTranslationByCountry,
  getLocalRegistrationDescriptionTranslationByCountry,
} from 'ha/utils/filters/registration';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { Grid, Text, Typography, AlertLabs, units } from '@hbf/dsl';
import { AlertCircleLight, CheckCircle1Light } from '@ha/icons';
import { FacilitiesValues } from 'ha/models/ListingFacilities/constants';

const useStyles = makeStyles()(() => ({
  registrationIcon: {
    fontSize: units.rem(0.875),
    marginTop: units.rem(0.3),
  },
  alertLabs: {
    padding: units.yx(0, 0.5),
    marginTop: units.rem(0.5),
    marginBottom: units.rem(1),
  },
  longParagraph: {
    marginBottom: 0,
  },
}));

export interface Props {
  registration?: string;
  countryCode: string;
}

const RegistrationInfoSectionInternal: React.FC<Props> = ({
  countryCode,
  registration = '',
}) => {
  const { T } = useIntl();
  const { classes } = useStyles();
  const { md } = useMediaQuery();

  if (!showRegistration(countryCode)) {
    return null;
  }

  const getRegistrationPossibleText = () => {
    switch (registration) {
      case FacilitiesValues.Yes: {
        return T('tenant.registration_yes');
      }
      case FacilitiesValues.No: {
        return T('tenant.registration_no');
      }
      default: {
        return T('tenant.registration_unknown');
      }
    }
  };

  return (
    <Grid item xs={12} data-test-locator="Registration Info Section">
      <AlertLabs
        icon={
          registration === FacilitiesValues.Yes ? (
            <CheckCircle1Light className={classes.registrationIcon} />
          ) : (
            <AlertCircleLight className={classes.registrationIcon} />
          )
        }
        severity="notification"
        className={classes.alertLabs}
      >
        <Typography.Caption>
          <Text kind="inline" variant="inherit" fw="bold">
            {getRegistrationPossibleText()}{' '}
            <Text italic kind="inline" variant="inherit">
              {`${T(getLocalRegistrationTranslationByCountry(countryCode))}. `}
            </Text>
          </Text>
          {md ? (
            T(getLocalRegistrationDescriptionTranslationByCountry(countryCode))
          ) : (
            <LongParagraph
              maxLength={0}
              ellipsis={false}
              className={classes.longParagraph}
            >
              {T(
                getLocalRegistrationDescriptionTranslationByCountry(
                  countryCode,
                ),
              )}
            </LongParagraph>
          )}
        </Typography.Caption>
      </AlertLabs>
    </Grid>
  );
};

const RegistrationInfoSection = React.memo(RegistrationInfoSectionInternal);
export { RegistrationInfoSection };
